import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import {
  AdmissionStepSubmitHandle,
  StudentAdmissionStepProps,
} from '../studentAdmission.interfaces';
import { Button, Grid, Typography, Divider } from '@mui/material';
import {
  GENDER,
  TUserDetails,
} from '../../../../../../../apis/users/users.interfaces';
import { ZodType } from 'zod';
import { z } from 'zod';
import {
  userDefaultValue,
  userValidationSchema,
} from '../../../../../../../utils/validationHelpers/userValidationSchema';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ControlledSelectField from '../../../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../../../../../../components/controlledComponents/ControlledTextField.component';
import ControlledCountryCodeSelectField from '../../../../../../components/controlledComponents/ControlledCountryCodeSelectField.component';
import ControlledFileUploadField from '../../../../../../components/controlledComponents/ControlledFileUploadField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import isAppWithMock from 'src/utils/isAppWithMock';

export type Step2FormData = {
  guardian1: Partial<TUserDetails>;
  guardian2?: Partial<TUserDetails>;
};

/** Admission flow: omit backend-only requirements so live validation can enable Continue */
const baseUserObj = userValidationSchema as unknown as z.ZodObject<
  Record<string, z.ZodTypeAny>
>;
const contactDetailsSchema = baseUserObj.shape.contactDetails as z.ZodObject<
  Record<string, z.ZodTypeAny>
>;
const addressSchema = contactDetailsSchema.shape.address as z.ZodObject<
  Record<string, z.ZodTypeAny>
>;
const basicDetailsSchema = baseUserObj.shape.basicDetails as z.ZodObject<
  Record<string, z.ZodTypeAny>
>;

const getAdmissionGuardianUserSchema = (
  translateText: (key: string) => string
) =>
  baseUserObj
    .omit({
      id: true,
      actions: true,
      contactDetails: true,
      basicDetails: true,
    })
    .extend({
      id: z.string().optional(),
      actions: z.object({
        created: z.object({
          userId: z.string().optional(),
          timestamp: z.number().int().positive(),
        }),
      }),
      basicDetails: basicDetailsSchema
        .omit({ gender: true })
        .extend({
          gender: z.preprocess(
            (val) =>
              val === null || val === undefined || val === ''
                ? 'select'
                : val,
            z
              .union([
                z.literal('select'),
                z.nativeEnum(GENDER, {
                  errorMap: () => ({
                    message: translateText(
                      'students.admission.validation.genderRequired'
                    ),
                  }),
                }),
              ])
              .refine((val) => val !== 'select', {
                message: translateText(
                  'students.admission.validation.genderRequired'
                ),
              })
          ),
        }),
      contactDetails: contactDetailsSchema
        .omit({ address: true, phone: true })
        .extend({
          // Guardian phone is stricter than the student's (Step 1): the BE wire schema
          // requires country code `+` + 1–4 digits and a phone of exactly 10 digits.
          phone: z.object({
            countryCode: z
              .string()
              .regex(/^\+[1-9]\d{0,3}$/, 'Country code must look like +91'),
            phoneNumber: z
              .string()
              .regex(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
            isVerified: z.boolean(),
          }),
          email: z.object({
            email: z
              .string()
              .trim()
              .min(
                1,
                translateText('students.admission.validation.emailEmpty')
              )
              .email(
                translateText('students.admission.validation.emailInvalid')
              )
              .max(
                255,
                translateText('students.admission.validation.emailTooLong')
              ),
            isVerified: z.boolean(),
          }),
          address: addressSchema
            .omit({ country: true, postalCode: true })
            .extend({
              country: z
                .union([
                  z.literal(''),
                  z
                    .string()
                    .min(2, 'Country must be at least 2 characters')
                    .max(100, 'Country must not exceed 100 characters')
                    .regex(
                      /^[A-Za-z\s]+$/,
                      'Country should only contain letters and spaces'
                    ),
                ])
                .optional(),
              // BE wire schema requires a 5- or 6-digit zip for guardians.
              postalCode: z
                .string()
                .regex(/^\d{5,6}$/, 'Zip code must be 5 or 6 digits'),
            }),
        }),
    });

function guardian2ToOptionalForValidation(g: unknown): unknown {
  const guardian = g as Step2FormData['guardian2'];
  if (guardian == null) return undefined;
  const fn = String(guardian.basicDetails?.firstName ?? '').trim();
  const ln = String(guardian.basicDetails?.lastName ?? '').trim();
  const email = String(guardian.contactDetails?.email?.email ?? '').trim();
  const phone = String(guardian.contactDetails?.phone?.phoneNumber ?? '').trim();
  if (!fn && !ln && !email && !phone) return undefined;
  return guardian;
}

const getAdmissionStep2Schema = (translateText: (key: string) => string) =>
  z.object({
    guardian1: getAdmissionGuardianUserSchema(translateText),
    guardian2: z.preprocess(
      guardian2ToOptionalForValidation,
      getAdmissionGuardianUserSchema(translateText).optional()
    ),
  }) as ZodType<Step2FormData>;

// Guardian slots are positional on the backend: guardian1 = father / primary,
// guardian2 = mother (see mapGuardianFormToGuardianDetailsPayload + the PUT
// /students/guardian-details upsert). In the add flow we pre-select the gender
// to match each slot; an explicit user choice still overrides it.
const defaultValues: Step2FormData = {
  guardian1: {
    ...userDefaultValue,
    basicDetails: {
      ...userDefaultValue.basicDetails,
      gender: GENDER.MALE,
    },
  },
  guardian2: {
    ...userDefaultValue,
    basicDetails: {
      ...userDefaultValue.basicDetails,
      gender: GENDER.FEMALE,
    },
  },
};

const AdmissionStep2GuardianDetailsView = forwardRef<
  AdmissionStepSubmitHandle,
  StudentAdmissionStepProps<Step2FormData>
>(function AdmissionStep2GuardianDetailsView(
  { onCompleteHandler, studentDetails, onStepValidityChange, onPersistStep },
  ref
) {
  const isMockData = isAppWithMock();
  const { translateText } = useTranslation();
  const admissionStep2Schema = useMemo(
    () => getAdmissionStep2Schema(translateText),
    [translateText]
  );
  const methods = useForm<Step2FormData>({
    resolver: zodResolver(admissionStep2Schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUseNativeValidation: false,
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    getValues,
    reset,
  } = methods;

  useImperativeHandle(ref, () => ({
    submitStep: () =>
      new Promise((resolve) => {
        if (!onPersistStep) {
          resolve(true);
          return;
        }
        void handleSubmit(
          async (data) => {
            try {
              await onPersistStep(data);
              resolve(true);
            } catch {
              resolve(false);
            }
          },
          () => resolve(false)
        )();
      }),
  }));

  useEffect(() => {
    onStepValidityChange?.(isValid);
  }, [isValid, onStepValidityChange]);

  const copyFromStudent = () => {
    const studentData = studentDetails?.contactDetails;
    if (studentData) {
      // Only copy contactDetails, profile is excluded
      setValue('guardian1.contactDetails', studentData, {
        shouldValidate: true,
      });
    }
  };

  const copyFromGuardian1 = () => {
    const currentValues = getValues();
    const guardian1Data = currentValues.guardian1;
    if (guardian1Data) {
      const guardian2Clone = JSON.parse(JSON.stringify(guardian1Data));
      // Explicitly exclude profile from being copied
      if (guardian2Clone.basicDetails?.profile) {
        delete guardian2Clone.basicDetails.profile;
      }
      const guardian2Existing = currentValues.guardian2;
      // Guardian 2 is a distinct person: preserve its own id rather than
      // inheriting Guardian 1's. The backend now upserts guardian slots
      // positionally (guardian1 = father, guardian2 = mother) and ignores the
      // per-guardian userId, so this keeps the form coherent — it is no longer
      // guarding against duplicate guardian records.
      guardian2Clone.id = guardian2Existing?.id;
      guardian2Clone.actions = guardian2Existing?.actions ?? guardian2Clone.actions;
      if (guardian2Clone.basicDetails) {
        guardian2Clone.basicDetails.firstName =
          guardian2Existing?.basicDetails?.firstName;
        guardian2Clone.basicDetails.gender =
          guardian2Existing?.basicDetails?.gender;
      }
      // Use reset to properly update all nested fields
      reset(
        {
          guardian1: currentValues.guardian1,
          guardian2: guardian2Clone,
        },
        { keepDefaultValues: false }
      );
    }
  };

  useEffect(() => {
    if (
      studentDetails?.guardianDetails &&
      studentDetails.guardianDetails.length > 0
    ) {
      const guardian1 = studentDetails.guardianDetails[0];
      const guardian2 =
        studentDetails.guardianDetails.length > 1
          ? studentDetails.guardianDetails[1]
          : null;

      reset({
        guardian1: {
          id: guardian1.id,
          basicDetails: guardian1.basicDetails,
          contactDetails: guardian1.contactDetails,
        },
        guardian2: guardian2
          ? {
            id: guardian2.id,
            basicDetails: guardian2.basicDetails,
            contactDetails: guardian2.contactDetails,
          }
          : undefined,
      });
    } else {
      reset(defaultValues);
    }
  }, [studentDetails, reset]);

  useEffect(() => {
    if (studentDetails && Object.keys(errors).length === 0) {
      const timeoutId = setTimeout(() => {
        onCompleteHandler();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [studentDetails, errors, onCompleteHandler]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit((data) => {
          void onPersistStep?.(data);
        })}
        style={{ width: '100%' }}
        id="admission-step-2-form"
      >
        <Grid
          container
          spacing={{ xs: 1, sm: 2 }}
          padding={{ xs: 1, sm: 2 }}
          data-testid="admission-step-2-form-container"
        >
          {/* Guardian 1 Section */}
          <Grid item xs={12}>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 1, sm: 2 },
                  mb: { xs: 1, sm: 0 },
                }}
                data-testid="guardian1-header"
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                  }}
                >
                  {translateText('students.admission.guardian1DetailsRequired')}
                </Typography>
                <Button
                  size="small"
                  onClick={copyFromStudent}
                  sx={{
                    minHeight: { xs: '44px', sm: '32px' },
                    fontSize: { xs: '16px', sm: '14px' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                  data-testid="copy-from-student-button"
                >
                  {translateText('students.admission.copyAddressFromStudent')}
                </Button>
              </Grid>

              {/* Basic Details */}
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian1-first-name"
                  required
                  label={translateText('students.admission.firstName')}
                  name="guardian1.basicDetails.firstName"
                  control={control}
                  errorMessage={
                    errors.guardian1?.basicDetails?.firstName?.message
                  }
                  isGridItem={false}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian1-middle-name"
                  label={translateText('students.admission.middleName')}
                  name="guardian1.basicDetails.middleName"
                  control={control}
                  errorMessage={
                    errors.guardian1?.basicDetails?.middleName?.message
                  }
                  isGridItem={false}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian1-last-name"
                  required
                  label={translateText('students.admission.lastName')}
                  name="guardian1.basicDetails.lastName"
                  control={control}
                  errorMessage={
                    errors.guardian1?.basicDetails?.lastName?.message
                  }
                  isGridItem={false}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledSelectField
                  label={translateText('students.admission.gender')}
                  id="gender"
                  name="guardian1.basicDetails.gender"
                  isRequired
                  control={control}
                  itemList={[
                    {
                      key: GENDER.MALE,
                      value: translateText('students.admission.male'),
                    },
                    {
                      key: GENDER.FEMALE,
                      value: translateText('students.admission.female'),
                    },
                    {
                      key: GENDER.OTHER,
                      value: translateText('students.admission.other'),
                    },
                  ]}
                  errorMessage={errors.guardian1?.basicDetails?.gender?.message}
                />
              </Grid>

              {/* Contact Details */}
              <Grid item xs={12} sm={4}>
                <ControlledCountryCodeSelectField<Step2FormData>
                  id="guardian1-phone-code-label"
                  isRequired
                  label={translateText('students.admission.code')}
                  name="guardian1.contactDetails.phone.countryCode"
                  control={control}
                  errorMessage={
                    errors.guardian1?.contactDetails?.phone?.countryCode
                      ?.message
                  }
                  isGridItem={false}
                  data-testid="admission-guardian1-phone-country-code"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian1-phone-number"
                  required
                  label={translateText('students.admission.phoneNumber')}
                  name="guardian1.contactDetails.phone.phoneNumber"
                  control={control}
                  errorMessage={
                    errors.guardian1?.contactDetails?.phone?.phoneNumber
                      ?.message
                  }
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="guardian1-email"
                  required
                  label={translateText('auth.email')}
                  type="email"
                  name="guardian1.contactDetails.email.email"
                  control={control}
                  errorMessage={
                    errors.guardian1?.contactDetails?.email?.email?.message
                  }
                  isGridItem={false}
                  dataTestId="admission-guardian1-email"
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <ControlledTextField
                  id="guardian1-address-line1"
                  required
                  label={translateText('students.admission.address1')}
                  name="guardian1.contactDetails.address.address1"
                  control={control}
                  errorMessage={
                    errors.guardian1?.contactDetails?.address?.address1?.message
                  }
                  multiline
                  rows={2}
                  isGridItem={false}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  id="guardian1-address-line2"
                  label={translateText('students.admission.address2')}
                  name="guardian1.contactDetails.address.address2"
                  control={control}
                  errorMessage={
                    errors.guardian1?.contactDetails?.address?.address2?.message
                  }
                  multiline
                  rows={2}
                  isGridItem={false}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian1-city"
                  required
                  label={translateText('students.admission.city')}
                  name="guardian1.contactDetails.address.city"
                  control={control}
                  errorMessage={
                    errors.guardian1?.contactDetails?.address?.city?.message
                  }
                  isGridItem={false}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian1-state"
                  required
                  label={translateText('students.admission.state')}
                  name="guardian1.contactDetails.address.state"
                  control={control}
                  errorMessage={
                    errors.guardian1?.contactDetails?.address?.state?.message
                  }
                  isGridItem={false}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian1-postal-code"
                  required
                  label={translateText('students.admission.postalCode')}
                  name="guardian1.contactDetails.address.postalCode"
                  control={control}
                  errorMessage={
                    errors.guardian1?.contactDetails?.address?.postalCode
                      ?.message
                  }
                  isGridItem={false}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <ControlledFileUploadField
                  label={translateText('students.admission.profilePicture')}
                  name="guardian1.basicDetails.profile.profilePicture"
                  control={control}
                  errorMessage={
                    errors.guardian1?.basicDetails?.profile?.profilePicture
                      ?.message
                  }
                  id="guardian1-profile-picture"
                  sectionTitle={translateText(
                    'students.admission.guardian1ProfilePicture'
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Guardian 2 Section */}
          <Grid
            item
            xs={12}
            mt={{ xs: 2, sm: 4 }}
            data-testid="guardian2-section"
          >
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 1, sm: 2 },
                  mb: { xs: 1, sm: 0 },
                }}
                data-testid="guardian2-header"
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                  }}
                >
                  {translateText('students.admission.guardian2DetailsOptional')}
                </Typography>
                <Button
                  size="small"
                  onClick={copyFromGuardian1}
                  sx={{
                    minHeight: { xs: '44px', sm: '32px' },
                    fontSize: { xs: '16px', sm: '14px' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                  data-testid="copy-from-guardian1-button"
                >
                  {translateText('students.admission.copyDetailsFromGuardian1')}
                </Button>
              </Grid>

              {/* Basic Details */}
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian2-first-name"
                  label={translateText('students.admission.firstName')}
                  name="guardian2.basicDetails.firstName"
                  control={control}
                  errorMessage={
                    errors.guardian2?.basicDetails?.firstName?.message
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian2-middle-name"
                  label={translateText('students.admission.middleName')}
                  name="guardian2.basicDetails.middleName"
                  control={control}
                  errorMessage={
                    errors.guardian2?.basicDetails?.middleName?.message
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian2-last-name"
                  label={translateText('students.admission.lastName')}
                  name="guardian2.basicDetails.lastName"
                  control={control}
                  errorMessage={
                    errors.guardian2?.basicDetails?.lastName?.message
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledSelectField
                  label={translateText('students.admission.gender')}
                  id="gender"
                  name="guardian2.basicDetails.gender"
                  control={control}
                  itemList={[
                    {
                      key: GENDER.MALE,
                      value: translateText('students.admission.male'),
                    },
                    {
                      key: GENDER.FEMALE,
                      value: translateText('students.admission.female'),
                    },
                    {
                      key: GENDER.OTHER,
                      value: translateText('students.admission.other'),
                    },
                  ]}
                  errorMessage={errors.guardian2?.basicDetails?.gender?.message}
                />
              </Grid>

              {/* Contact Details */}
              <Grid item xs={12} sm={4}>
                <ControlledCountryCodeSelectField<Step2FormData>
                  id="guardian2-phone-code-label"
                  label={translateText('students.admission.code')}
                  name="guardian2.contactDetails.phone.countryCode"
                  control={control}
                  errorMessage={
                    errors.guardian2?.contactDetails?.phone?.countryCode
                      ?.message
                  }
                  isGridItem={false}
                  data-testid="admission-guardian2-phone-country-code"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian2-phone-number"
                  label={translateText('students.admission.phoneNumber')}
                  name="guardian2.contactDetails.phone.phoneNumber"
                  control={control}
                  errorMessage={
                    errors.guardian2?.contactDetails?.phone?.phoneNumber
                      ?.message
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  id="guardian2-email"
                  label={translateText('auth.email')}
                  type="email"
                  name="guardian2.contactDetails.email.email"
                  control={control}
                  errorMessage={
                    errors.guardian2?.contactDetails?.email?.email?.message
                  }
                  dataTestId="admission-guardian2-email"
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <ControlledTextField
                  id="guardian2-address-line1"
                  label={translateText('students.admission.address1')}
                  name="guardian2.contactDetails.address.address1"
                  control={control}
                  errorMessage={
                    errors.guardian2?.contactDetails?.address?.address1?.message
                  }
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  id="guardian2-address-line2"
                  label={translateText('students.admission.address2')}
                  name="guardian2.contactDetails.address.address2"
                  control={control}
                  errorMessage={
                    errors.guardian2?.contactDetails?.address?.address2?.message
                  }
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian2-city"
                  label={translateText('students.admission.city')}
                  name="guardian2.contactDetails.address.city"
                  control={control}
                  errorMessage={
                    errors.guardian2?.contactDetails?.address?.city?.message
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian2-state"
                  label={translateText('students.admission.state')}
                  name="guardian2.contactDetails.address.state"
                  control={control}
                  errorMessage={
                    errors.guardian2?.contactDetails?.address?.state?.message
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  id="guardian2-postal-code"
                  label={translateText('students.admission.postalCode')}
                  name="guardian2.contactDetails.address.postalCode"
                  control={control}
                  errorMessage={
                    errors.guardian2?.contactDetails?.address?.postalCode
                      ?.message
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <ControlledFileUploadField
                  label={translateText('students.admission.profilePicture')}
                  name="guardian2.basicDetails.profile.profilePicture"
                  control={control}
                  errorMessage={
                    errors.guardian2?.basicDetails?.profile?.profilePicture
                      ?.message
                  }
                  id="guardian2-profile-picture"
                  sectionTitle={translateText(
                    'students.admission.guardian2ProfilePicture'
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {!studentDetails ? (
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
              data-testid="admission-step-2-actions"
            >
              {isMockData ? (
                <Button
                  onClick={() => onCompleteHandler()}
                  sx={{
                    minHeight: { xs: '44px', sm: '36px' },
                    fontSize: { xs: '16px', sm: '14px' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                  data-testid="admission-step-2-finish-button"
                >
                  {translateText('students.admission.finishStep2')}
                </Button>
              ) : null}
            </Grid>
          ) : null}
        </Grid>
      </form>
    </FormProvider>
  );
});

export default AdmissionStep2GuardianDetailsView;
